import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import './MarkdownEditor.css';

interface MarkdownEditorProps {
  onPrintDocument: (content: string) => void;
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({ onPrintDocument }) => {
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  
  // Load saved content from local storage on component mount
  useEffect(() => {
    const savedContent = localStorage.getItem('markdownContent');
    if (savedContent) {
      setMarkdownContent(savedContent);
    } else {
      // Default starter content
      setMarkdownContent(`# Receipt Title

## Customer Information
**Name:** Customer Name
**Date:** ${new Date().toLocaleDateString()}

---

This is a sample receipt with **bold text** and *italic text*.

You can create lists:
- Item 1
- Item 2
- Item 3

## Thank You!
Visit us again!`);
    }
  }, []);

  // Save content to local storage whenever it changes
  useEffect(() => {
    localStorage.setItem('markdownContent', markdownContent);
  }, [markdownContent]);

  const handlePrint = () => {
    onPrintDocument(markdownContent);
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear the editor? This cannot be undone.')) {
      setMarkdownContent('');
    }
  };

  const togglePreview = () => {
    setPreviewMode(!previewMode);
  };

  return (
    <div className="markdown-editor-container">
      <h2>Freehand Document Editor</h2>
      
      <div className="editor-toolbar">
        <button 
          className="toolbar-button"
          onClick={togglePreview}
        >
          {previewMode ? 'Edit Mode' : 'Preview Mode'}
        </button>
        <button 
          className="toolbar-button"
          onClick={handlePrint}
        >
          Print Document
        </button>
        <button 
          className="toolbar-button clear-button"
          onClick={handleClear}
        >
          Clear
        </button>
      </div>
      
      <div className="editor-content">
        {previewMode ? (
          <div className="markdown-preview">
            <ReactMarkdown>{markdownContent}</ReactMarkdown>
          </div>
        ) : (
          <textarea
            className="markdown-textarea"
            value={markdownContent}
            onChange={(e) => setMarkdownContent(e.target.value)}
            placeholder="Write your markdown content here..."
          />
        )}
      </div>
      
      <div className="markdown-help">
        <h3>Markdown Tips:</h3>
        <ul>
          <li><code># Heading 1</code> - Creates a large heading</li>
          <li><code>## Heading 2</code> - Creates a medium heading</li>
          <li><code>**bold text**</code> - Makes text <strong>bold</strong></li>
          <li><code>*italic text*</code> - Makes text <em>italic</em></li>
          <li><code>- item</code> - Creates a bullet point</li>
          <li><code>1. item</code> - Creates a numbered list</li>
          <li><code>---</code> - Creates a horizontal line</li>
        </ul>
      </div>
    </div>
  );
};

export default MarkdownEditor;
